import React, {
  memo,
  useMemo,
} from 'react';

import {
  Button,
  Typography,
} from 'antd';
import { ImArrowRight2 } from 'react-icons/im';

import assets from '../../../../assets';
import { useTheme } from '../../../../context/ThemeProvider';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import {
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  getTQProfileQueryAttemptStatus,
} from '../../../../shared/tqConnectUtils';
import {
  dynamicItemTitle,
  tintColor,
} from '../../../../shared/utils';
import { QuestionAttemptStatus } from '../../../../types/assignmentTypes';
import { ROUTES } from '../../../../types/routes';
import {
  ITQProfileCompletionQueryWithResponse,
} from '../../../../types/tqConnectTypes/user';
import TQConnectIcon from '../TQConnectIcon';

enum TQConnectSetupStatus {
  TQ_SETUP = 'tq-setup',
  TQ_SETUP_INCOMPLETE = 'tq-setup-incomplete',
  TQ_SETUP_NOT_ENABLED_IN_MANGOES = 'tq-setup-not-enabled-in-mangoes',
  PROFILE_SETUP = 'profile-setup',
  PROFILE_SETUP_INCOMPLETE = 'profile-setup-incomplete',
}

type TQConnectSetupStatusType = `${TQConnectSetupStatus}`;

interface TQConnectSetupCardContent {
  title: string;
  subtitle: string;
  buttonText: string;
}

const TQConnectFeedCard: React.FC = () => {
  const { colors: COLORS } = useTheme();
  const navigate = useAppNavigate();

  const { data: tqConnectConfigResult } = useTQConnectConfig();

  const { isDefaultConfig, tqConnectConfig } = tqConnectConfigResult || {};
  const {
    setupCompleted,
    enabled,
    label,
    allowedMangoes,
    profileCompletionQueries,
    scoreConfig,
  } = tqConnectConfig || {};
  const { name: tqScoreName } = scoreConfig || {};

  const { tqConnectProfileCompletionResponses } = useAppSelector(
    (state) => state.user,
  );
  const {
    userCommunityAccess: {
      hasCommunityCreatorAccess,
      hasTQConnectAccessForUser,
    },
  } = useAppSelector((state) => state.community);

  const status: TQConnectSetupStatusType = useMemo(() => {
    // check if tq connect config is not fully setup but default config was updated
    // if (!enabled && !isDefaultConfig)
    //   return TQConnectSetupStatus.TQ_SETUP_INCOMPLETE;

    // check if tq connect is set up but not enabled in any mangoes but config was updated
    if (
      !isDefaultConfig &&
      setupCompleted &&
      (!allowedMangoes || allowedMangoes.length === 0)
    )
      return TQConnectSetupStatus.TQ_SETUP_NOT_ENABLED_IN_MANGOES;

    // check if user has completed some profile completion queries and some are still pending
    if (
      hasTQConnectAccessForUser &&
      tqConnectProfileCompletionResponses &&
      profileCompletionQueries &&
      tqConnectProfileCompletionResponses.length <
        profileCompletionQueries?.length
    )
      return TQConnectSetupStatus.PROFILE_SETUP_INCOMPLETE;

    return hasCommunityCreatorAccess
      ? TQConnectSetupStatus.TQ_SETUP
      : TQConnectSetupStatus.PROFILE_SETUP;
  }, [
    allowedMangoes,
    // enabled,
    setupCompleted,
    hasCommunityCreatorAccess,
    isDefaultConfig,
    profileCompletionQueries,
    tqConnectProfileCompletionResponses,
    hasTQConnectAccessForUser,
  ]);

  const CONTENT: Record<TQConnectSetupStatus, TQConnectSetupCardContent> =
    useMemo(
      () => ({
        [TQConnectSetupStatus.TQ_SETUP]: {
          title: `Introducing ${label}`,
          subtitle:
            'Define how your members earn credibility, not just points.',
          buttonText: 'Get started',
        },
        [TQConnectSetupStatus.TQ_SETUP_INCOMPLETE]: {
          title: `${label} - Complete setup`,
          subtitle: `Enable ${tqScoreName} to allow your members to earn credibility.`,
          buttonText: 'Complete setup',
        },
        [TQConnectSetupStatus.TQ_SETUP_NOT_ENABLED_IN_MANGOES]: {
          title: `${tqScoreName} is set up — But not live yet!`,
          subtitle: `You’ve successfully created your ${label} system, but it hasn’t been enabled for any ${dynamicItemTitle('mangoes')} yet.`,
          buttonText: `Enable ${tqScoreName} in ${dynamicItemTitle('mangoes')}`,
        },
        [TQConnectSetupStatus.PROFILE_SETUP]: {
          title: `Introducing ${label}`,
          subtitle:
            'Complete your profile to unlock your trust journey and start earning credibility!',
          buttonText: 'Complete profile',
        },
        [TQConnectSetupStatus.PROFILE_SETUP_INCOMPLETE]: {
          title: `${label} - Complete profile`,
          subtitle:
            'Complete your profile to unlock your trust journey and start earning credibility!',
          buttonText: 'Complete profile',
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [label, tqScoreName],
    );

  const tqProfileCompletionQueriesWithResponse = useMemo<
    ITQProfileCompletionQueryWithResponse[]
  >(() => {
    if (!profileCompletionQueries) return [];

    return profileCompletionQueries.map((question) => {
      const answer = tqConnectProfileCompletionResponses?.find(
        (q) => q.questionId === question._id,
      );

      return {
        ...question,
        questionId: question._id,
        question: question.question,
        order: question.order,
        selectedOptions: answer?.selectedOptions,
        subjectiveAnswer: answer?.subjectiveAnswer,
      };
    });
  }, [tqConnectProfileCompletionResponses, profileCompletionQueries]);

  const questionsMap = useMemo<{
    [key: string]: ITQProfileCompletionQueryWithResponse;
  }>(() => {
    return tqProfileCompletionQueriesWithResponse?.reduce(
      (acc, question) => {
        return {
          ...acc,
          [question._id]: {
            ...question,
            questionId: question._id,
          },
        };
      },
      {} as { [key: string]: ITQProfileCompletionQueryWithResponse },
    );
  }, [tqProfileCompletionQueriesWithResponse]);

  const firstUnattemptedQuestion = profileCompletionQueries?.find(
    (q) =>
      getTQProfileQueryAttemptStatus(questionsMap[q._id]) ===
      QuestionAttemptStatus.SKIPPED,
  );

  const renderCard = () => {
    // remove tq connect feed card:
    // case 1: tq connect config is available
    // case 2: user has neither community creator access nor tq connect access for user
    // case 3: tq connect is enabled and user has community creator access
    // case 4: tq connect is enabled and user has tq connect access for user and all profile completion queries are completed
    if (
      !tqConnectConfigResult ||
      (!hasCommunityCreatorAccess && !hasTQConnectAccessForUser) ||
      (enabled && hasCommunityCreatorAccess) ||
      (hasTQConnectAccessForUser &&
        tqConnectProfileCompletionResponses &&
        profileCompletionQueries &&
        tqConnectProfileCompletionResponses.length ===
          profileCompletionQueries?.length)
    ) {
      return null;
    }

    return (
      <>
        <div
          className="feed-card__tq-connect"
          style={{
            ...(status === TQConnectSetupStatus.TQ_SETUP_NOT_ENABLED_IN_MANGOES
              ? {
                  borderColor: COLORS.DANGER,
                  backgroundColor: tintColor(COLORS.DANGER, 90),
                }
              : {}),
          }}>
          {status ===
          TQConnectSetupStatus.TQ_SETUP_NOT_ENABLED_IN_MANGOES ? null : (
            <div className="card__preview">
              <img
                src={assets.svg.tqConnectSetupPreview}
                alt="Setup Preview"
                className="card__preview__image"
              />
            </div>
          )}
          <div className="card__content">
            <div className="content__title">
              <TQConnectIcon className="content__title__icon" size={32} />
              <Typography.Title level={5} className="content__title__text">
                {CONTENT[status].title}
              </Typography.Title>
            </div>
            <Typography.Paragraph
              className="content__text"
              style={{ marginBottom: 0 }}>
              {CONTENT[status].subtitle}
            </Typography.Paragraph>
            <Button
              type="primary"
              className="siteBtn siteBtnPrimary feed-card__tq-connect__button"
              onClick={() => {
                navigate(
                  ROUTES.TQ_CONNECT_PROFILE_COMPLETE_QUESTIONS,
                  {},
                  {
                    search: `initialQuestionNo=${firstUnattemptedQuestion?.order}`,
                  },
                );
              }}>
              <ImArrowRight2 size={16} style={{ color: '#fff' }} />
              {CONTENT[status].buttonText}
            </Button>
          </div>
        </div>
      </>
    );
  };

  return renderCard();
};

export default memo(TQConnectFeedCard);
